import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { defer, from, retry, switchMap, throwError, timer } from "rxjs";
import { environment } from "@env";
import { Core } from '@core/core';

export const apiSessionInterceptor: HttpInterceptorFn = (req, next) =>
{
    if (!isApiRequest(req.url)) return next(req);
    const translateService = inject(TranslateService);
    const core = inject(Core);
    return from(forward(req, next, translateService, core)).pipe(switchMap(events => events));
};

function isApiRequest(url: string): boolean
{
    if (url.startsWith('/api/')) return true;
    try
    {
        const request = new URL(url);
        const configured = new URL(environment.office_api_host);
        return request.origin === configured.origin && request.pathname.startsWith('/api/');
    }
    catch { return false; }
}

const forward = async (req: HttpRequest<unknown>, next: HttpHandlerFn, translateService: TranslateService, core: Core) =>
{
    const userLang = translateService.currentLang || translateService.defaultLang || 'en';
    let headers = req.headers.delete('X-Session-Id').delete('X-Device-Hash')
        .set('X-User-Lang', userLang);
    if (!headers.has('Accept-Language')) headers = headers.set('Accept-Language', userLang);
    const pathname = new URL(req.url, location.origin).pathname;
    const bootstrap = pathname === '/api/auth/session/exchange' || pathname === '/api/auth/session/rotate'
        || pathname === '/api/auth/session'
        || pathname === '/api/auth/logout';
    const session = core.session;
    if (!bootstrap && !headers.has('Authorization'))
    {
        let token = session?.getAccessToken();
        if (!token && session)
        {
            await session.check();
            token = session.getAccessToken();
        }
        if (token) headers = headers.set('Authorization', `Bearer ${ token }`);
    }
    const token = session?.getAccessToken();
    return defer(() =>
    {
        if (core.session !== session || session?.getAccessToken() !== token)
        {
            return throwError(() => new Error('Office API request superseded by session change'));
        }
        return next(req.clone({ headers }));
    }).pipe(
        retry({
            count: 5,
            delay: (error, attempt) => error?.status === 429
                ? timer(1_000 * 2 ** (attempt - 1))
                : throwError(() => error),
        }),
    );
};
